import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Mountain, Trees, Flame, Wifi, ChefHat, Coffee, Car, Bath, ArrowRight } from "lucide-react";

const iconMap = { wifi: Wifi, flame: Flame, campfire: Flame, "chef-hat": ChefHat, thermometer: Coffee, mountain: Mountain, car: Car, bath: Bath, garden: Trees };

function ExperienceCard({ e, i }) {
  return (
    <div
      className="rounded-2xl overflow-hidden transition-transform duration-300 hover:-translate-y-1"
      style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)" }}
      data-testid={`experience-${i}`}
    >
      <img src={e.img} alt={e.title} className="w-full aspect-[4/3] object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
      <div className="p-5">
        <div className="font-serif text-xl mb-2">{e.title}</div>
        <p className="text-sm opacity-80 leading-relaxed">{e.desc}</p>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [data, setData] = useState(null);
  useEffect(() => { http.get("/public/property").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="min-h-screen flex items-center justify-center font-serif text-2xl">Loading Freebird Nest…</div>;
  const p = data.property;
  const byCat = (cat, title) =>
    data.gallery.find((g) => g.category === cat && (!title || g.title === title))?.image_url || "";
  const BEDROOM = byCat("Rooms");
  const NATURE = byCat("Nature");
  const BONFIRE = byCat("Experiences");
  const roomsImgs = data.gallery.filter((g) => g.category === "Rooms");
  const ATTIC = roomsImgs.find((g) => g.title === "Attic Bedroom")?.image_url || "";
  const BEDROOM2 = roomsImgs.find((g) => g.title === "Cozy Wooden Room")?.image_url || "";
  const LOFT = data.gallery.find((g) => g.category === "Interiors")?.image_url || "";
  const COMMON = data.gallery.find((g) => g.category === "Common Areas")?.image_url || "";
  const VILLAGE = data.gallery.filter((g) => g.category === "Nature").find((g) => g.title.includes("Village"))?.image_url || "";

  return (
    <div>
      <PublicHeader />

      {/* 1. HERO — DAYTIME VILLA */}
      <section className="relative overflow-hidden" style={{ minHeight: "92vh" }}>
        <img
          src={p.hero_image}
          alt="Freebird Nest — daytime view of the wooden Himalayan villa"
          className="absolute inset-0 w-full h-full object-cover object-[50%_40%] md:object-center"
          data-testid="hero-image"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(20,23,21,.05) 0%, rgba(20,23,21,.25) 55%, rgba(20,23,21,.68) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-24 pb-16 md:pt-44 md:pb-24">
          <div className="max-w-3xl text-white fadein">
            <span className="badge-terracotta" style={{ background: "rgba(200,90,50,.9)", color: "#fff", border: "none" }} data-testid="hero-badge">Gadagushaini · Himachal Pradesh</span>
            <h1 className="h1-hero text-5xl md:text-7xl mt-6 mb-2 uppercase tracking-wide" data-testid="hero-headline">Freebird Nest</h1>
            <div className="font-serif text-2xl md:text-4xl mb-6 italic opacity-95" data-testid="hero-tagline">{p.tagline}</div>
            <p className="text-base md:text-lg opacity-90 max-w-2xl leading-relaxed" style={{ fontWeight: 300 }} data-testid="hero-subtext">
              A peaceful 4-bedroom Himalayan retreat surrounded by forests, streams, terraced fields and village life in Himachal Pradesh.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/availability" data-testid="hero-check-availability" className="btn-primary">Check Availability</Link>
              <Link to="/rooms" data-testid="hero-explore-villa" className="btn-outline" style={{ borderColor: "#fff", color: "#fff", background: "rgba(20,23,21,.35)", backdropFilter: "blur(6px)" }}>Explore the Villa</Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. ABOUT */}
      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-20 md:py-28 text-center">
        <div className="eyebrow mb-4">About</div>
        <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-8">A Different Kind of Mountain Stay</h2>
        <div className="space-y-5 text-base md:text-lg leading-relaxed text-left md:text-center" style={{ color: "var(--muted)" }}>
          <p>Freebird Nest is a peaceful mountain retreat surrounded by the natural beauty of Himachal Pradesh.</p>
          <p>Here, the experience is not just about the room. It is about waking up to mountain landscapes, walking beside streams, enjoying the quiet of the village, sitting around a bonfire at night and slowing down away from the rush of city life.</p>
          <p>The property combines warm wooden interiors with an open natural setting, giving guests a comfortable base from which to experience the mountains and the surrounding village landscape.</p>
        </div>
        <p className="serif-quote text-2xl mt-8" style={{ color: "var(--pine)" }}>Come for the mountains. Stay for the peace. Leave with memories.</p>
      </section>

      {/* 3. STAY — BEDROOM FEATURE */}
      <section className="py-20 md:py-28" style={{ background: "var(--surface)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div className="overflow-hidden rounded-2xl">
            <img src={BEDROOM} alt="Warm wooden bedroom at Freebird Nest" className="w-full aspect-[16/10] object-cover" loading="lazy" data-testid="stay-bedroom-image" />
          </div>
          <div>
            <div className="eyebrow mb-3">Stay With Us</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-5">Warm Wooden Interiors</h2>
            <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: "var(--muted)" }}>
              Comfortable rooms surrounded by natural wood and the warmth of a mountain home. Freebird Nest has four bedrooms — a family duplex, a king room, a duplex private cottage and a suite room — each kept simple, warm and connected to the surroundings.
            </p>
            <ul className="space-y-2 mb-8">
              {data.rooms.map((r) => (
                <li key={r.id} className="flex items-center gap-3" data-testid={`home-room-${r.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: "var(--terracotta)" }} />
                  <span className="font-serif text-lg">{r.name}</span>
                </li>
              ))}
            </ul>
            <Link to="/rooms" data-testid="home-view-all-suites" className="btn-outline inline-flex items-center gap-2">View the rooms <ArrowRight size={16} /></Link>
          </div>
        </div>

        {(ATTIC || BEDROOM2) && (
          <div className="grid grid-cols-2 gap-4 md:gap-6 mt-10 max-w-4xl mx-auto">
            {ATTIC && (
              <figure data-testid="stay-attic-image" className="overflow-hidden rounded-2xl">
                <img src={ATTIC} alt="Attic bedroom with sloping wooden ceiling at Freebird Nest" className="w-full aspect-[4/3] object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                <figcaption className="mt-3 text-sm font-serif" style={{ color: "var(--muted)" }}>Mountain character — natural wood, warm interiors and spaces connected to the mountains.</figcaption>
              </figure>
            )}
            {BEDROOM2 && (
              <figure data-testid="stay-bedroom2-image" className="overflow-hidden rounded-2xl">
                <img src={BEDROOM2} alt="Cozy wooden bedroom at Freebird Nest" className="w-full aspect-[4/3] object-cover transition-transform duration-500 hover:scale-105" loading="lazy" />
                <figcaption className="mt-3 text-sm font-serif" style={{ color: "var(--muted)" }}>Simple, warm rooms with the quiet of the forest outside.</figcaption>
              </figure>
            )}
          </div>
        )}
      </section>

      {/* 4. NATURE — STREAM / GREEN VALLEY FEATURE */}
      <section className="relative overflow-hidden" style={{ minHeight: "78vh" }}>
        <img src={NATURE} alt="Stream and green valley near Freebird Nest" className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-testid="nature-image" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(20,23,21,.7) 0%, rgba(20,23,21,.25) 60%, rgba(20,23,21,.05) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-28 md:py-40">
          <div className="max-w-xl text-white fadein">
            <div className="eyebrow mb-4" style={{ color: "#EAA14D" }}>Explore</div>
            <h2 className="font-serif text-4xl md:text-6xl mb-6">Nature at Your Doorstep</h2>
            <p className="text-base md:text-lg opacity-90 leading-relaxed" style={{ fontWeight: 300 }}>
              Step outside into streams, forests, terraced fields and the peaceful rhythm of Himalayan village life.
            </p>
            <Link to="/attractions" data-testid="nature-explore-link" className="btn-outline inline-flex items-center gap-2 mt-8" style={{ borderColor: "#fff", color: "#fff", background: "rgba(20,23,21,.35)", backdropFilter: "blur(6px)" }}>Explore around Freebird Nest <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* 5. BONFIRE FEATURE */}
      <section className="relative overflow-hidden" style={{ minHeight: "78vh" }}>
        <img src={BONFIRE} alt="Guests around a bonfire at Freebird Nest" className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-testid="bonfire-image" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(20,23,21,.72) 0%, rgba(20,23,21,.25) 60%, rgba(20,23,21,.05) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-28 md:py-40">
          <div className="max-w-xl text-white fadein">
            <div className="eyebrow mb-4" style={{ color: "#EAA14D" }}>After Sunset</div>
            <h2 className="font-serif text-4xl md:text-6xl mb-6">Evenings Around the Fire</h2>
            <p className="text-base md:text-lg opacity-90 leading-relaxed" style={{ fontWeight: 300 }}>
              When the mountains grow quiet, gather around the bonfire, share stories and enjoy an evening under the Himalayan sky.
            </p>
          </div>
        </div>
      </section>

      {/* 5b. SPACE TO SLOW DOWN — music/common area */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div className="order-2 md:order-1">
            <div className="eyebrow mb-3">Inside</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-5">Space to Slow Down</h2>
            <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: "var(--muted)" }}>
              Read, work, make music, have a conversation or simply enjoy a quiet corner surrounded by the mountains. The common spaces at Freebird Nest are made for slow days — a book, a melody, a good conversation.
            </p>
            <p className="serif-quote text-xl" style={{ color: "var(--pine)" }}>Nature is the luxury here.</p>
          </div>
          <div className="order-1 md:order-2 overflow-hidden rounded-2xl">
            <img src={COMMON} alt="Music and reading corner in the wooden common area at Freebird Nest" className="w-full aspect-[16/10] object-cover" loading="lazy" data-testid="common-area-image" />
          </div>
        </div>
      </section>

      {/* 5c. BUILT WITH CHARACTER — loft / triangular window */}
      <section className="py-20 md:py-28" style={{ background: "var(--surface)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div className="overflow-hidden rounded-2xl">
            <img src={LOFT} alt="Loft with large triangular window at Freebird Nest" className="w-full aspect-[16/10] object-cover" loading="lazy" data-testid="loft-image" />
          </div>
          <div>
            <div className="eyebrow mb-3">Architecture</div>
            <h2 className="font-serif text-4xl md:text-5xl mb-5">Built with Character</h2>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: "var(--muted)" }}>
              Wood, light and mountain surroundings give Freebird Nest its distinctive sense of place — from the sloping attic ceilings to the big triangular window that frames the hillside.
            </p>
          </div>
        </div>
      </section>

      {/* 6. EXPLORE THE VALLEY — village stream walk */}
      <section className="relative overflow-hidden" style={{ minHeight: "78vh" }}>
        <img src={VILLAGE} alt="Village path and stream in the valley near Freebird Nest" className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-testid="village-walk-image" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(20,23,21,.7) 0%, rgba(20,23,21,.25) 60%, rgba(20,23,21,.05) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-28 md:py-40">
          <div className="max-w-xl text-white fadein">
            <div className="eyebrow mb-4" style={{ color: "#EAA14D" }}>Explore the Valley</div>
            <h2 className="font-serif text-4xl md:text-6xl mb-6">Step Into the Quieter Side of Himachal</h2>
            <p className="text-base md:text-lg opacity-90 leading-relaxed" style={{ fontWeight: 300 }}>
              Walk through the surrounding landscape, discover village paths, mountain streams and the everyday beauty of life in the valley.
            </p>
          </div>
        </div>
      </section>

      {/* 7. EXPERIENCES */}
      <section className="py-20 md:py-28" style={{ background: "var(--pine)", color: "#F5F2EC" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="eyebrow mb-3" style={{ color: "#EAA14D" }}>Experiences</div>
            <h2 className="font-serif text-4xl md:text-5xl">More than a place to sleep</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "Bonfire Evenings", desc: "Gather around the fire with friends, family or fellow travellers and enjoy the quiet mountain evening.", img: BONFIRE },
              { title: "Nature Walks", desc: "Follow village paths through forests, fields and the surrounding Himalayan landscape.", img: NATURE },
              { title: "Streamside Moments", desc: "Slow down beside mountain streams and the sound of flowing water around the valley.", img: byCat("Experiences", "Streamside Moments") },
            ].map((e, i) => (
              <ExperienceCard key={e.title} e={e} i={i} />
            ))}
            <div className="sm:col-span-2 lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-5 lg:max-w-[66.6%] lg:mx-auto w-full">
              {[
                { title: "Village & Local Life", desc: "Experience the quieter side of Himachal through village paths, farms and everyday mountain life.", img: byCat("Experiences", "Village & Local Life") },
                { title: "Slow & Creative Evenings", desc: "Share a conversation, play some music, read or simply spend an unhurried evening together.", img: byCat("Experiences", "Slow & Creative Evenings") },
              ].map((e, i) => (
                <ExperienceCard key={e.title} e={e} i={i + 3} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 8. AMENITIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 md:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="eyebrow mb-3">Included</div>
          <h2 className="font-serif text-4xl md:text-5xl">Simple comforts, thoughtfully kept</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {data.amenities.map((a, i) => {
            const Icon = iconMap[a.icon] || Coffee;
            return (
              <div key={a.id} className="card text-center" data-testid={`home-amenity-${i}`}>
                <div className="inline-flex w-12 h-12 rounded-full items-center justify-center mb-3" style={{ background: "var(--terracotta-subtle)", color: "var(--terracotta)" }}>
                  <Icon size={22} />
                </div>
                <div className="font-serif text-lg">{a.name}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 9. CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pb-20">
        <div className="card text-center" style={{ background: "var(--pine)", color: "#F5F2EC", border: "none", padding: "56px 24px" }}>
          <div className="eyebrow mb-3" style={{ color: "#EAA14D" }}>Leave the city behind</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-4">Ready for the mountains?</h2>
          <p className="opacity-80 mb-8 max-w-xl mx-auto">Come slow down, breathe fresh mountain air and experience Freebird Nest.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/availability" data-testid="home-cta-check-availability" className="btn-primary">Check Availability</Link>
            <a href={p.airbnb_url} target="_blank" rel="noopener noreferrer" data-testid="home-cta-airbnb" className="btn-outline" style={{ borderColor: "#EAA14D", color: "#EAA14D" }}>Book Your Stay</a>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
